import React, { useCallback, useState } from 'react';
import { usersApi } from '@api/usersApi/usersApi';
import { ApiError, OAuthResponse, UserToken } from '@api/RestClient';
import { useToastContext } from './toastContext';
import { getLocalStorageItem } from '@utils/storageUtils';

const getLocalStorageUserToken = () => getLocalStorageItem('Replan.userToken');

type AuthContextType = {
    token: UserToken | null;
    user: string;
    runLogin: (email: string, password: string) => Promise<void>;
    runRefreshToken: any;
    runLogout: any;
    generateResetToken: (email: string) => Promise<void>;
    changeUserPassword: (args: {
        login: string;
        password: string;
        passwordRepeat: string;
        token: string;
    }) => Promise<unknown>;
    signingIn: boolean;
    signingOut: boolean;
};

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
    const ctx = React.useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used inside AuthProvider');
    }
    return ctx;
};

export const AuthProvider = ({ children }) => {
    const [signingIn, setSigningIn] = useState<boolean>(false);
    const [signingOut, setSigningOut] = useState<boolean>(false);
    const [token, setToken] = React.useState<UserToken | null>(getLocalStorageUserToken);
    const [user, setUser] = React.useState<string>(getLocalStorageItem('Replan.userName'));
    const { addToast } = useToastContext();

    const handleLogin = async (email: string, password: string) => {
        setSigningIn(true);
        const oAuthResponse: OAuthResponse | ApiError = await usersApi.login(email, password);
        setSigningIn(false);

        if ('error' in oAuthResponse) {
            addToast(
                `${oAuthResponse.error?.['error_description'] ?? oAuthResponse?.error_description}`,
                'error'
            );
            setToken(null);
            return;
        }

        localStorage.setItem('Replan.userToken', JSON.stringify(oAuthResponse));
        localStorage.setItem('Replan.userName', email);
        const token = getLocalStorageUserToken();
        setToken(token);
        setUser(email);
    };

    const handleRefreshToken = async () => {
        const newToken = await usersApi.refreshToken();

        if (newToken && !('error' in newToken)) {
            localStorage.setItem('Replan.userToken', JSON.stringify(newToken));
            setToken(newToken);
        }
    };

    const handleLogout = async () => {
        setSigningOut(true);
        await usersApi.logout();
        localStorage.removeItem('Replan.userToken');
        localStorage.removeItem('Replan.userName');
        sessionStorage.removeItem('Replan.EntityDataByEntityName');
        setToken(null);
        setUser('');
        setSigningOut(false);
    };

    const generateResetToken = useCallback(async (email: string) => {
        const ac = new AbortController();
        const resp = await usersApi.generateResetToken({
            data: { emailOrLogin: email },
            abortSignal: ac.signal,
        });
    }, []);

    const changeUserPassword = useCallback(
        async ({
            login,
            password,
            passwordRepeat,
            token,
        }: {
            login: string;
            password: string;
            passwordRepeat: string;
            token: string;
        }) => {
            const ac = new AbortController();
            return await usersApi.changeUserPassword({
                data: {
                    passwordRecoveryRequest: {
                        emailOrLogin: login,
                        password,
                        passwordRepeat,
                        token,
                    },
                },
                abortSignal: ac.signal,
            });
        },
        []
    );

    const value = {
        token,
        user,
        runLogin: handleLogin,
        runRefreshToken: handleRefreshToken,
        runLogout: handleLogout,
        generateResetToken,
        changeUserPassword,
        signingIn,
        signingOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
